import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface Groomer {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  branchId: string | null;
}

export function useGroomers() {
  return useQuery({
    queryKey: ['users', 'groomers'],
    queryFn: async (): Promise<Groomer[]> => {
      // A groomer is any active user in the tenant; RLS filters tenant.
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id,first_name,last_name,avatar_url,branch_id,status')
        .eq('status', 'ACTIVE')
        .order('first_name');
      if (error) throw error;
      return (data ?? []).map((u) => ({
        id: u.id,
        firstName: u.first_name,
        lastName: u.last_name,
        avatarUrl: u.avatar_url,
        branchId: u.branch_id,
      }));
    },
  });
}

export interface Branch {
  id: string;
  name: string;
  isDefault: boolean;
  city: string | null;
  address: string | null;
  phone: string | null;
  timezone: string;
}

export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: async (): Promise<Branch[]> => {
      const { data, error } = await supabase
        .from('branches')
        .select('id,name,is_default,city,address,phone,timezone')
        .is('deleted_at', null)
        .order('is_default', { ascending: false })
        .order('name');
      if (error) throw error;
      return (data ?? []).map((b) => ({
        id: b.id,
        name: b.name,
        isDefault: b.is_default,
        city: b.city,
        address: b.address,
        phone: b.phone,
        timezone: b.timezone,
      }));
    },
  });
}

export interface BranchInput {
  name: string;
  city?: string;
  address?: string;
  phone?: string;
  timezone?: string;
  isDefault?: boolean;
}

export function useUpsertBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string } & BranchInput) => {
      // Setting a branch as default unsets others (DB doesn't enforce, we do it client-side)
      if (input.isDefault) {
        await supabase.from('branches').update({ is_default: false }).neq('id', input.id ?? '');
      }
      const row: Record<string, unknown> = {
        name: input.name,
        city: input.city ?? null,
        address: input.address ?? null,
        phone: input.phone ?? null,
        timezone: input.timezone ?? 'America/Bogota',
        is_default: input.isDefault ?? false,
      };
      const { data, error } = input.id
        ? await supabase.from('branches').update(row).eq('id', input.id).select().single()
        : await supabase.from('branches').insert(row).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branches'] }),
  });
}

export function useDeleteBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('soft_delete_branch', { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branches'] }),
  });
}
